import React, { useState, useEffect, useRef } from "react";
import { Drawer, Button, List, Typography, Space, Spin, Alert, Modal } from "antd";
import { PlusOutlined, MessageOutlined, DeleteOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useChatSessions, useCreateSession, useDeleteSession, ChatSession } from "./hooks";
import styled from "styled-components";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {colors} from "../../../util/colors.ts";

const { Text } = Typography;

const SessionItem = styled(List.Item)`
  cursor: pointer;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
  
  &:hover {
    background-color: #fafafa;
    
    .delete-button {
      opacity: 1;
    }
  }
  
  &.active {
    background-color: ${colors.darkBg};
    border-left: 3px solid ${colors.main};
  }
`;

const DeleteButton = styled(Button)`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  &:hover {
    color: #ff4d4f;
    border-color: #ff4d4f;
  }

  @media (max-width: 991px) {
    opacity: 1;
  }
`;

const SessionTitle = styled(Text)`
    font-weight: 400;
    margin-bottom: 4px;
    display: flex;
    font-size: 14px;
`;

const SessionDate = styled(Text)`
  color: #8c8c8c;
  font-size: 12px;
`;

const NewChatButton = styled(Button)`
  width: 100%;
  margin-bottom: 16px;
  height: 40px;
`;

interface ChatSessionsSidebarProps {
  visible: boolean;
  onClose: () => void;
  onSessionSelect: (session: ChatSession) => void;
  onNewChat: (sessionId: string) => void;
  activeSessionId?: string;
  conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax';
  alwaysVisible?: boolean; // New prop for fixed sidebar mode
  collapsed?: boolean; // Collapse state
  onToggle?: () => void; // Toggle collapse
}

const FixedSidebarContainer = styled.div<{ collapsed?: boolean }>`
  width: ${({ collapsed }) => (collapsed ? '48px' : '320px')};
  min-width: ${({ collapsed }) => (collapsed ? '48px' : '320px')};
  height: 100%;
  background-color: white;
  border-left: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;

  @media (max-width: 991px) {
    display: none;
  }
`;

const SidebarHeader = styled.div<{ collapsed?: boolean }>`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'space-between')};
  min-height: 56px;
`;

const SidebarContent = styled.div<{ collapsed?: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  opacity: ${({ collapsed }) => (collapsed ? '0' : '1')};
  transition: opacity 0.2s ease;
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};
`;

export const ChatSessionsSidebar: React.FC<ChatSessionsSidebarProps> = ({
  visible,
  onClose,
  onSessionSelect,
  onNewChat,
  activeSessionId,
  conversationType,
  alwaysVisible = false,
  collapsed = false,
  onToggle
}) => {
  const navigate = useNavigate();
  const { sessions, isLoading, error, refetch } = useChatSessions(conversationType);
  const { createSession, isCreating, error: createError } = useCreateSession(conversationType);
  const { deleteSession } = useDeleteSession(conversationType);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  // Track previous visible state to detect when sidebar opens
  const prevVisibleRef = useRef(visible);

  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      refetch();
    }
    prevVisibleRef.current = visible;
  }, [visible, refetch, conversationType]);

  const handleCreateSession = () => {
    createSession(undefined, {
      onSuccess: (data) => {
        onNewChat(data.sessionId);
        onClose();
      }
    });
  };

  const handleDeleteSession = (sessionId: string, sessionTitle: string) => {
    Modal.confirm({
      title: 'Sitzung löschen',
      content: `Möchten Sie die Sitzung "${sessionTitle || 'Unbenannte Sitzung'}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      okText: 'Löschen',
      okType: 'danger',
      cancelText: 'Abbrechen',
      onOk: () => {
        const isActiveSession = activeSessionId === sessionId;

        // Set loading state for this specific session
        setDeletingSessionId(sessionId);

        deleteSession(sessionId.toString(), {
          onSuccess: () => {
            // Clear loading state
            setDeletingSessionId(null);
            // Refetch to get updated session list
            refetch().then(() => {
              // Handle navigation after successful deletion
              handlePostDeleteNavigation(sessionId, isActiveSession);
            });
          },
          onError: () => {
            // Clear loading state on error
            setDeletingSessionId(null);
          }
        });
      }
    });
  };

  const handlePostDeleteNavigation = (deletedSessionId: string, wasActiveSession: boolean) => {
    if (!wasActiveSession) {
      // If we didn't delete the active session, no navigation needed
      return;
    }

    // Filter out the deleted session from current sessions
    const remainingSessions = sessions.filter((session: ChatSession) => session.id !== deletedSessionId);

    if (remainingSessions.length > 0) {
      // Navigate to the most recent remaining session
      const mostRecentSession = remainingSessions.sort((a: ChatSession, b: ChatSession) => b.updated_at - a.updated_at)[0];
      const basePath = conversationType === 'job_interview' ? '/interview'
        : conversationType === 'job_letter' ? '/cover-letter'
        : conversationType === 'job_search' ? '/ai-jobs'
        : conversationType === 'job_tax' ? '/rentensteuerberater'
        : '/job-coach';
      navigate(`${basePath}/${mostRecentSession.id}`);
      onClose();
    } else {
      // No sessions remain - create a new one
      createSession(undefined, {
        onSuccess: (data) => {
          const basePath = conversationType === 'job_interview' ? '/interview'
        : conversationType === 'job_letter' ? '/cover-letter'
        : conversationType === 'job_search' ? '/ai-jobs'
        : conversationType === 'job_tax' ? '/rentensteuerberater'
        : '/job-coach';
          navigate(`${basePath}/${data.sessionId}`);
          onClose();
        }
      });
    }
  };

  const formatDate = (timestamp: number | string) => {
    if (!timestamp) {
      return 'Unbekannt';
    }

    try {
      let date: Date;

      if (typeof timestamp === 'string') {
        date = parseISO(timestamp);
      } else if (typeof timestamp === 'number') {
        if (timestamp > 1000000000000) {
          date = new Date(timestamp);
        } else {
          date = new Date(timestamp * 1000);
        }
      } else {
        return 'Unbekannt';
      }

      if (isNaN(date.getTime())) {
        return 'Unbekannt';
      }

      return `Letzte Nachricht: ${formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: de 
      })}`;
    } catch {
      return 'Unbekannt';
    }
  };

  const getNewChatButtonText = () => {
    switch (conversationType) {
      case 'job_coach':
        return 'Neuer Chat';
      case 'job_interview':
        return 'Neue Vorbereitung';
      case 'job_letter':
        return 'Neues Anschreiben';
      case 'job_search':
        return 'Neue Suche';
      case 'job_tax':
        return 'Neue Beratung';
      default:
        return 'Neue Unterhaltung';
    }
  };

  const getSidebarTitle = () => {
    switch (conversationType) {
      case 'job_coach':
        return 'Job Coach';
      case 'job_interview':
        return 'Interviews';
      case 'job_letter':
        return 'Anschreiben';
      case 'job_search':
        return 'Jobsuchen';
      case 'job_tax':
        return 'Steuer & Rente';
      default:
        return 'Chat Sitzungen';
    }
  };

  const sidebarContent = (
    <Space direction="vertical" style={{ width: '100%', paddingTop: 30 }}>
      <NewChatButton
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleCreateSession}
        loading={isCreating}
      >
        {getNewChatButtonText()}
      </NewChatButton>

      {createError && (
        <Alert
          message="Fehler beim Erstellen einer neuen Sitzung"
          type="error"
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Lade Sitzungen...</Text>
          </div>
        </div>
      ) : error ? (
        <Alert
          message="Fehler beim Laden der Sitzungen"
          description="Bitte versuchen Sie es erneut."
          type="error"
          action={
            <Button size="small" onClick={() => refetch()}>
              Erneut laden
            </Button>
          }
        />
      ) : sessions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <MessageOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Keine Sitzungen gefunden</Text>
          </div>
        </div>
      ) : (
        <List
          dataSource={sessions}
          renderItem={(session: ChatSession) => (
            <SessionItem
              key={session.id}
              className={activeSessionId === session.id ? 'active' : ''}
              onClick={() => {
                onSessionSelect(session);
                if (!alwaysVisible) {
                  onClose();
                }
              }}
            >
              <ChatSessionWrapper>
                <SessionTitle>
                  {session.title || 'Job Coach Sitzung'}
                </SessionTitle>
                <SessionDate>
                  {formatDate(session.updated_at)}
                </SessionDate>
              </ChatSessionWrapper>
              <DeleteButton
                className="delete-button"
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingSessionId === session.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSession(session.id, session.title);
                }}
              />
            </SessionItem>
          )}
        />
      )}
    </Space>
  );

  // Render as fixed sidebar if alwaysVisible is true
  if (alwaysVisible) {
    return (
      <FixedSidebarContainer collapsed={collapsed}>
        <SidebarHeader collapsed={collapsed}>
          {!collapsed && <span>{getSidebarTitle()}</span>}
          {onToggle && (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onToggle}
              title={collapsed ? 'Chat-Verlauf anzeigen' : 'Chat-Verlauf ausblenden'}
            />
          )}
        </SidebarHeader>
        <SidebarContent collapsed={collapsed}>
          {sidebarContent}
        </SidebarContent>
      </FixedSidebarContainer>
    );
  }

  // Render as drawer for backward compatibility
  return (
    <StyledDrawer
      title={getSidebarTitle()}
      placement="right"
      onClose={onClose}
      open={visible}
      width={320}
    >
      {sidebarContent}
    </StyledDrawer>
  );
};

const ChatSessionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    flex-wrap: wrap;
    padding-right: 40px;
`;

const StyledDrawer = styled(Drawer)`
    @media (max-width: 991px) {
        .ant-drawer-content-wrapper {
            width: 100% !important;
        }
    }
`;
